import React, { useMemo } from 'react';
// Simulated external site content
const HERO_CONTENT = `
  Specialist SEND Tuition
  Across the UK
  One-to-one support for children and young people aged 4–19. Online and in-person tuition that builds confidence, reduces barriers, and helps learners thrive.

  Find Support for My Child
  Schools & Councils
  DBS Checked Tutors
  Free Initial Assessment
  UK-Wide Coverage`;
const ABOUT_CONTENT = `
  Kirsty Fox, Founder of Love2Learn Education
  About us
  At Love2Learn Education, we specialise in supporting children and young people with SEND through personalised tuition.

  Founded by Kirsty Fox, our team of nearly 40 specialist tutors bring expertise in autism, ADHD, PDA, dyslexia, EBSA and more. Every tutor holds an enhanced DBS, is experienced in supporting SEND learners and is dedicated to helping learners thrive.

  About Love2Learn Education`;
const SERVICES_CONTENT = `
  Why Families Choose Us
  At Love2Learn Education we know that every learner is different. Our tuition is designed to meet children and young people where they are, removing barriers and creating space for progress.

  Specialist SEND Tutors
  Experienced in autism, ADHD, PDA, dyslexia, EBSA and more.

  Integrated Support
  Incorporating therapy strategies, movement breaks, and interest-led projects.

  Personalised Learning Plans
  Tailored to each child's needs, EHCP targets, or EOTAS package.

  Trusted by Parents and Schools
  Consistently recommended for our expertise and care.

  Flexible Delivery
  Online with Pencil Spaces or in-person at home or school.

  Regular Progress Updates
  Written reports after each session to keep everyone informed.`;
function splitNonEmptyLines(text) {
    return text.split('\n').map(l => l.trim()).filter(Boolean);
}
function HeroSection({ content, baseKey }) {
    const lines = splitNonEmptyLines(content);
    const heading = lines[0] || 'Welcome';
    const subheading = lines[1] || '';
    const bullets = lines.slice(2);
    return (React.createElement("section", { "data-navigationKey": baseKey, className: "rounded-xl overflow-hidden" },
        React.createElement("div", { className: "bg-gradient-to-br from-indigo-500 via-violet-500 to-pink-500 p-6 md:p-8" },
            React.createElement("div", { className: "md:w-3/4" },
                React.createElement("h1", { className: "text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight" }, heading),
                subheading && React.createElement("p", { className: "text-white/90 mb-5 text-sm md:text-base" }, subheading),
                bullets.length > 0 && (React.createElement("ul", { className: "grid grid-cols-1 sm:grid-cols-2 gap-2" }, bullets.slice(0, 6).map((b, i) => (React.createElement("li", { key: i, "data-navigationKey": `${baseKey}.bullet.${i}`, className: "text-sm text-white/90 flex items-start gap-2" },
                    React.createElement("span", { className: "mt-1 h-1.5 w-1.5 rounded-full bg-white/90" }),
                    React.createElement("span", null, b)))))),
                React.createElement("div", { className: "mt-6 flex gap-3" },
                    React.createElement("button", { "data-navigationKey": `${baseKey}.cta.getStarted`, className: "px-4 py-2 rounded-md bg-white text-indigo-700 text-sm font-medium hover:bg-white/90 transition-colors shadow-sm" }, "Get Started"),
                    React.createElement("button", { "data-navigationKey": `${baseKey}.cta.learnMore`, className: "px-4 py-2 rounded-md border border-white/30 text-white text-sm hover:bg-white/10 transition-colors" }, "Learn More"))))));
}
function AboutSection({ content, baseKey }) {
    const paragraphs = splitNonEmptyLines(content);
    return (React.createElement("section", { "data-navigationKey": baseKey, className: "rounded-xl border border-white/10 bg-white/5 backdrop-blur p-6" },
        React.createElement("h2", { className: "text-lg font-semibold text-white mb-3" }, "About Us"),
        React.createElement("div", { className: "space-y-3" }, paragraphs.map((p, i) => (React.createElement("p", { key: i, "data-navigationKey": `${baseKey}.paragraph.${i}`, className: "text-slate-200/90 text-sm leading-relaxed" }, p))))));
}
function ServicesSection({ content, baseKey }) {
    const lines = splitNonEmptyLines(content);
    const heading = lines[0] || 'Services';
    const intro = lines[1] || '';
    const items = lines.slice(2);
    return (React.createElement("section", { "data-navigationKey": baseKey, className: "rounded-xl border border-white/10 bg-white/5 backdrop-blur p-6" },
        React.createElement("h2", { className: "text-lg font-semibold text-white mb-3" }, heading),
        intro && React.createElement("p", { className: "text-slate-200/90 text-sm mb-4" }, intro),
        React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" }, items.map((it, i) => (React.createElement("div", { key: i, "data-navigationKey": `${baseKey}.item.${i}`, className: "rounded-lg p-3 bg-white/5 border border-white/10" },
            React.createElement("div", { className: "text-slate-100 text-sm" }, it)))))));
}
export const MiniSite = () => {
    const sections = useMemo(() => ([
        { id: 'nav.topWidget', title: 'Overview' },
        { id: 'nav.aboutUs', title: 'About Us' },
        { id: 'nav.services', title: 'Services' },
    ]), []);
    const scrollTo = (id) => {
        const el = document.querySelector(`[data-navigationKey="${id}"]`);
        if (el)
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    return (React.createElement("div", { "data-navigationKey": "site", className: "rounded-2xl overflow-hidden border border-slate-700 shadow-2xl bg-[#0b1220]" },
        React.createElement("div", { "data-navigationKey": "header", className: "flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/80" },
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("div", { "data-navigationKey": "header.logo", className: "h-6 w-6 rounded bg-gradient-to-br from-cyan-400 to-blue-600" }),
                React.createElement("div", { "data-navigationKey": "header.brand", className: "text-white font-semibold tracking-tight" }, "NovaLearn")),
            React.createElement("div", { className: "hidden md:flex items-center gap-6 text-slate-300 text-sm" },
                React.createElement("span", { "data-navigationKey": "header.nav.solutions", className: "hover:text-white cursor-pointer" }, "Solutions"),
                React.createElement("span", { "data-navigationKey": "header.nav.pricing", className: "hover:text-white cursor-pointer" }, "Pricing"),
                React.createElement("span", { "data-navigationKey": "header.nav.contact", className: "hover:text-white cursor-pointer" }, "Contact"),
                React.createElement("button", { "data-navigationKey": "header.cta.getStarted", className: "px-3 py-1.5 rounded-md bg-cyan-500 text-white text-sm hover:bg-cyan-600" }, "Get Started"))),
        React.createElement("div", { className: "min-h-[400px] grid grid-cols-12 gap-3 p-4" },
            React.createElement("aside", { "data-navigationKey": "nav", className: "col-span-3 lg:col-span-2 sticky top-16 self-start" },
                React.createElement("div", { "data-navigationKey": "nav.title", className: "text-xs uppercase tracking-wide text-slate-400 mb-2" }, "On this page"),
                React.createElement("div", { className: "flex flex-col space-y-1" }, sections.map((s) => (React.createElement("button", { key: s.id, "data-navigationKey": s.id, onClick: () => scrollTo(s.id), className: "text-left px-2 py-1 rounded transition-colors text-slate-300 hover:bg-white/10 hover:text-white" }, s.title))))),
            React.createElement("main", { className: "col-span-9 lg:col-span-10 space-y-8" },
                React.createElement(HeroSection, { baseKey: "topWidget", content: HERO_CONTENT }),
                React.createElement(AboutSection, { baseKey: "aboutUs", content: ABOUT_CONTENT }),
                React.createElement(ServicesSection, { baseKey: "services", content: SERVICES_CONTENT })))));
};
export default MiniSite;
